import React, { useMemo, useState } from "react";
import { Dialog } from "@headlessui/react";
import { Eye, EyeOff, KeyRound, LockKeyhole, LogIn, ShieldCheck } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { logout, updateCurrentUser } from "../../store/authSlice";
import { authStorage } from "../../utils/authStorage";

const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,14}$/;

const getLoginPath = (user) => {
  if (Number(user?.user_type) === 0) return "/superadmin/login";
  if (Number(user?.user_type) === 1) return "/admin/login";
  return "/login";
};

const PasswordField = ({
  id,
  label,
  placeholder,
  icon: Icon,
  value,
  onChange,
  visible,
  onToggle,
  autoFocus = false,
}) => (
  <label className="force-password__field" htmlFor={id}>
    <div className="force-password__input-wrap">
      <span className="force-password__field-icon">
        <Icon size={20} />
      </span>
      <div className="force-password__input-content">
        <span className="force-password__label">{label}</span>
        <input
          id={id}
          name={id}
          type={visible ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          autoFocus={autoFocus}
          autoComplete="new-password"
          required
        />
      </div>
      <button type="button" onClick={onToggle} aria-label={visible ? "Hide password" : "Show password"}>
        {visible ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
  </label>
);

export default function ForcePasswordChangeModal() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reduxUser = useSelector((state) => state.auth.user);
  const user = reduxUser || authStorage.getUser();
  const isOpen = Boolean(user?.must_change_password);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const canSubmit = useMemo(() => {
    return currentPassword && newPassword && confirmPassword && !isSaving;
  }, [confirmPassword, currentPassword, isSaving, newPassword]);

  if (!isOpen) return null;

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError("");
    setIsSaving(false);
  };

  const backToSignIn = () => {
    const loginPath = getLoginPath(user);
    resetForm();
    dispatch(logout());
    navigate(loginPath, { replace: true });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (newPassword !== confirmPassword) {
      setError("New passwords do not match.");
      return;
    }

    if (!passwordRegex.test(newPassword)) {
      setError("Use 6-14 characters with uppercase, lowercase, number, and symbol.");
      return;
    }

    setIsSaving(true);
    try {
      const response = await axiosInstance.patch("/admin/users/change-password", {
        currentPassword,
        newPassword,
        confirmPassword,
      });
      const nextUser = {
        ...(user || {}),
        ...(response.data?.user || {}),
        must_change_password: false,
      };
      dispatch(updateCurrentUser(nextUser));
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || "Password could not be changed. Please try again.");
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={() => {}} className="force-password">
      <div className="force-password__backdrop" aria-hidden="true" />
      <div className="force-password__shell">
        <Dialog.Panel className="force-password__panel">
          <div className="force-password__header">
            <div className="force-password__icon">
              <ShieldCheck size={28} />
            </div>
            <div>
              <p className="force-password__eyebrow">Account Security</p>
              <Dialog.Title className="force-password__title">Change temporary password</Dialog.Title>
              <p className="force-password__copy">
                You signed in with a temporary password. Create your own password before continuing.
              </p>
            </div>
          </div>

          <form className="force-password__form" onSubmit={handleSubmit} autoComplete="off">
            <PasswordField
              id={`temporary-password-${user?.id || "account"}`}
              label="Temporary Password"
              placeholder="Enter temporary password"
              icon={LockKeyhole}
              value={currentPassword}
              onChange={setCurrentPassword}
              visible={showCurrent}
              onToggle={() => setShowCurrent((value) => !value)}
              autoFocus
            />
            <PasswordField
              id={`new-password-${user?.id || "account"}`}
              label="New Password"
              placeholder="Create a new password"
              icon={KeyRound}
              value={newPassword}
              onChange={setNewPassword}
              visible={showNew}
              onToggle={() => setShowNew((value) => !value)}
            />
            <PasswordField
              id={`confirm-new-password-${user?.id || "account"}`}
              label="Confirm Password"
              placeholder="Confirm new password"
              icon={ShieldCheck}
              value={confirmPassword}
              onChange={setConfirmPassword}
              visible={showConfirm}
              onToggle={() => setShowConfirm((value) => !value)}
            />

            <div className="force-password__hint">
              <KeyRound size={18} />
              <span>Password must include uppercase, lowercase, number, and symbol.</span>
            </div>

            {error && <div className="force-password__error">{error}</div>}

            <div className="force-password__actions">
              <button type="button" className="force-password__secondary" onClick={backToSignIn}>
                <LogIn size={18} />
                Back to Sign In
              </button>
              <button type="submit" className="force-password__primary" disabled={!canSubmit}>
                {isSaving ? "Changing..." : "Change Password"}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
